import { useEffect, useState, useMemo } from "react";
import { Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import SelectField from "../../components/os/SelectField";
import { input, mxn, GrHeader } from "./_grShared";

const PACKAGES = [{ value: "essential", label: "Essential", price: 0 }, { value: "premium", label: "Premium", price: 60000 }, { value: "luxury", label: "Luxury", price: 150000 }, { value: "designer", label: "Designer Collection", price: 280000 }];
const ITEMS = [
  { key: "pisos", label: "Pisos", options: [{ label: "Cerámica", price: 0 }, { label: "Porcelanato", price: 22000 }, { label: "Madera de ingeniería", price: 55000 }] },
  { key: "cocina", label: "Cubierta de cocina", options: [{ label: "Laminado", price: 0 }, { label: "Granito", price: 28000 }, { label: "Cuarzo", price: 48000 }] },
  { key: "muebles", label: "Muebles de cocina", options: [{ label: "Melamina", price: 0 }, { label: "Chapa de madera", price: 35000 }, { label: "Lacado premium", price: 68000 }] },
  { key: "banos", label: "Grifería de baños", options: [{ label: "Estándar", price: 0 }, { label: "Premium", price: 12000 }, { label: "Designer", price: 26000 }] },
  { key: "iluminacion", label: "Iluminación", options: [{ label: "Básica", price: 0 }, { label: "LED integrada", price: 18000 }, { label: "Domótica", price: 42000 }] },
];

export default function GrShowroom() {
  const [pkg, setPkg] = useState("essential");
  const [sel, setSel] = useState({});
  const [property, setProperty] = useState("");
  const [client, setClient] = useState("");
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/gr/showroom").then((r) => setSaved(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const total = useMemo(() => {
    const p = PACKAGES.find((x) => x.value === pkg)?.price || 0;
    return p + ITEMS.reduce((s, it) => s + (sel[it.key]?.price || 0), 0);
  }, [pkg, sel]);

  const save = async () => {
    if (!property.trim()) return toast.error("Propiedad requerida");
    const items = ITEMS.map((it) => ({ space: it.label, option: sel[it.key]?.label || it.options[0].label, price: sel[it.key]?.price || 0 }));
    try { await api.post("/gr/showroom", { property, client, package: pkg, items, total }); toast.success("Configuración guardada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <OSLayout title="OMNIA Virtual Showroom">
      <div className="space-y-6" data-testid="gr-showroom-page">
        <GrHeader title="OMNIA Virtual Showroom" desc="Explora y personaliza acabados, materiales y equipamiento con precio en tiempo real." />

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 card-surface p-5 space-y-4">
            <div><label className="text-xs text-navy/50 mb-1 block">Paquete de acabados</label><SelectField testid="gr-sr-package" value={pkg} onChange={setPkg} options={PACKAGES.map((p) => ({ value: p.value, label: `${p.label} (+${mxn(p.price)})` }))} /></div>
            {ITEMS.map((it) => (
              <div key={it.key}>
                <label className="text-xs text-navy/50 mb-1 block">{it.label}</label>
                <div className="flex flex-wrap gap-2" data-testid={`gr-sr-${it.key}`}>
                  {it.options.map((o) => {
                    const on = (sel[it.key]?.label || it.options[0].label) === o.label;
                    return <button key={o.label} onClick={() => setSel({ ...sel, [it.key]: o })} className={`px-3 py-2 rounded-lg border text-sm transition-colors ${on ? "border-violet bg-violet/10 text-violet" : "border-navy/10 hover:border-violet/40"}`} data-testid={`gr-sr-opt-${it.key}-${o.label}`}>{o.label} <span className="text-xs opacity-60">+{mxn(o.price)}</span></button>;
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="card-surface p-5 h-fit sticky top-4">
            <div className="overline text-navy/40 mb-1">Total de acabados</div>
            <div className="font-editorial text-4xl text-violet mb-4" data-testid="gr-sr-total">{mxn(total)}</div>
            <div className="space-y-2">
              <input className={input} placeholder="Propiedad" value={property} onChange={(e) => setProperty(e.target.value)} data-testid="gr-sr-property" />
              <input className={input} placeholder="Cliente" value={client} onChange={(e) => setClient(e.target.value)} />
              <button onClick={save} className="btn btn-primary w-full" data-testid="gr-sr-save"><Save className="w-4 h-4" /> Guardar configuración</button>
            </div>
          </div>
        </div>

        {loading ? null : saved.length > 0 && (
          <div className="card-surface p-4" data-testid="gr-sr-saved">
            <div className="overline text-navy/40 mb-3">Configuraciones guardadas</div>
            <div className="space-y-1.5">{saved.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm border-b border-navy/5 py-1.5">
                <span><b>{s.property}</b>{s.client ? ` · ${s.client}` : ""} <span className="badge badge-violet ml-1">{PACKAGES.find((p) => p.value === s.package)?.label}</span></span>
                <span className="flex items-center gap-3"><span className="text-violet font-medium">{mxn(s.total)}</span><button onClick={async () => { await api.delete(`/gr/showroom/${s.id}`); load(); }} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button></span>
              </div>
            ))}</div>
          </div>
        )}
      </div>
    </OSLayout>
  );
}
